import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { lovable } from '@/integrations/lovable';
import { Logo } from '@/components/Logo';
import { supabase } from '@/integrations/supabase/client';
import { FlaskConical, ShieldCheck, Briefcase, User } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const { loginDemo, signInWithPassword, signUpWithPassword, resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'real' ? 'real' : 'demo';

  // Demo state
  const [demoRole, setDemoRole] = useState<UserRole>('manager');

  // Real state
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const roles: { value: UserRole; label: string; Icon: typeof Briefcase }[] = [
    { value: 'manager', label: 'Manager', Icon: Briefcase },
    { value: 'rep', label: 'Sales Rep', Icon: User },
    { value: 'admin', label: 'Admin', Icon: ShieldCheck },
  ];

  const handleDemo = (e: React.FormEvent) => {
    e.preventDefault();
    loginDemo(demoRole);
    navigate(demoRole === 'rep' ? '/rep' : '/dashboard');
  };

  const handleReal = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        const { error } = await signUpWithPassword(email, password, fullName);
        if (error) return toast.error(error);
        toast.success("Account created. We've set up your private workspace.");
        // Best-effort signup telemetry (session may not be ready yet; retry briefly).
        setTimeout(async () => {
          const { data } = await supabase.auth.getSession();
          const u = data.session?.user;
          if (!u) return;
          const { data: profile } = await supabase.from('profiles').select('dealership_id').eq('id', u.id).maybeSingle();
          if (profile?.dealership_id) {
            await supabase.from('usage_events').insert({
              user_id: u.id,
              dealership_id: profile.dealership_id,
              event_name: 'signup',
              properties: {} as never,
            });
          }
        }, 800);
        // Solo signups land on /rep; AuthContext loads dealership info async,
        // RepDashboard works for solo users (they hold both manager + rep roles).
        navigate('/rep');
      } else {
        const { error } = await signInWithPassword(email, password);
        if (error) return toast.error(error);
        // Route by role: managers/admins → /dashboard, reps → /rep.
        const { data } = await supabase.auth.getSession();
        const u = data.session?.user;
        let dest = '/rep';
        if (u) {
          const { data: roles } = await supabase
            .from('user_roles').select('role').eq('user_id', u.id);
          const hasMgr = (roles ?? []).some(r => r.role === 'manager' || r.role === 'admin');
          dest = hasMgr ? '/dashboard' : '/rep';
        }
        setTimeout(async () => {
          const { data: s2 } = await supabase.auth.getSession();
          const u2 = s2.session?.user;
          if (!u2) return;
          const { data: profile } = await supabase.from('profiles').select('dealership_id').eq('id', u2.id).maybeSingle();
          if (profile?.dealership_id) {
            await supabase.from('usage_events').insert({
              user_id: u2.id,
              dealership_id: profile.dealership_id,
              event_name: 'login',
              properties: {} as never,
            });
          }
        }, 400);
        navigate(dest);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    const result = await lovable.auth.signInWithOAuth('google', {
      redirect_uri: `${window.location.origin}/dashboard`,
    });
    if (result.error) toast.error(result.error.message ?? 'Google sign-in failed');
  };

  const handleForgot = async () => {
    if (!email) return toast.error('Enter your email first');
    const { error } = await resetPassword(email);
    if (error) return toast.error(error);
    toast.success('Password reset email sent');
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-8 bg-background overflow-hidden">
      {/* Ultra-subtle automotive background texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(ellipse at center, hsl(220 60% 8% / 0.45), transparent 70%), repeating-conic-gradient(hsl(0 0% 100% / 0.06) 0% 25%, transparent 0% 50%)',
          backgroundSize: 'auto, 28px 28px',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, black 0%, transparent 65%)',
          maskImage:
            'radial-gradient(ellipse at center, black 0%, transparent 65%)',
        }}
      />
      <Card
        className="relative w-full max-w-md bg-[hsl(220,60%,8%)]/95 backdrop-blur-sm border-border/60"
        style={{
          boxShadow:
            '0 0 0 1px hsl(var(--border) / 0.6), 0 30px 80px -20px hsl(220 60% 2% / 0.9), 0 0 60px -30px hsl(var(--primary) / 0.25)',
        }}
      >
        <CardHeader className="text-center pb-8">
          <Link to="/" aria-label="PullOver.App home" className="inline-block mx-auto mb-3">
            <Logo variant="full" className="h-16 object-contain mx-auto" />
          </Link>
          <CardTitle className="font-display text-2xl">Access Your Sales Floor</CardTitle>
          <CardDescription>Select Demo Mode or Secure Dealership Login</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={initialTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full mb-8">
              <TabsTrigger value="demo">Demo</TabsTrigger>
              <TabsTrigger value="real">Secure Login</TabsTrigger>
            </TabsList>

            {/* DEMO TAB */}
            <TabsContent value="demo">
              <form onSubmit={handleDemo} className="space-y-5">
                <div
                  role="status"
                  className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
                >
                  <FlaskConical className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold uppercase tracking-wide">You're entering Demo Mode</div>
                    <div className="text-amber-200/80">
                      Sample data for "My Dealership". Nothing you log here is saved.
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Choose Demo Role</p>
                  <div className="grid grid-cols-3 gap-2">
                    {roles.map(({ value, label, Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setDemoRole(value)}
                        className={`flex flex-col items-center gap-1 px-3 py-3 rounded-md text-sm font-medium transition-colors border ${
                          demoRole === value
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-secondary text-secondary-foreground border-border hover:bg-accent'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">My Dealership · mock data · resets on refresh</p>
                </div>
                <Button type="submit" className="w-full" size="lg">
                  Enter Demo as {roles.find(r => r.value === demoRole)?.label}
                </Button>
                <p className="text-xs text-center text-muted-foreground">No account needed</p>
              </form>
            </TabsContent>

            {/* REAL TAB */}
            <TabsContent value="real">
              <form onSubmit={handleReal} className="space-y-4">
                <div
                  role="status"
                  className="flex items-start gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-xs text-primary-foreground"
                >
                  <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <div>
                    <div className="font-semibold uppercase tracking-wide text-primary">
                      You're entering Real Mode
                    </div>
                    <div className="text-foreground/80">
                      Live data · everything you log is saved to your dealership workspace.
                    </div>
                    <div className="text-foreground/60 mt-1">Protected dealership workspace · secure cloud sync</div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {mode === 'signup'
                    ? 'No dealership? You\u2019ll get your own private solo workspace.'
                    : 'Live data · persistent · multi-dealership'}
                </p>

                {mode === 'signup' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Full name</label>
                    <Input value={fullName} onChange={e => setFullName(e.target.value)} required />
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    type="email"
                    placeholder="you@dealership.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Password</label>
                    {mode === 'signin' && (
                      <button
                        type="button"
                        onClick={handleForgot}
                        className="text-xs text-primary hover:underline"
                      >
                        Forgot?
                      </button>
                    )}
                  </div>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                  {submitting ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Sign in'}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 bg-secondary/40 hover:bg-secondary border-border/60 text-foreground gap-2"
                  onClick={handleGoogle}
                >
                  <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden>
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
                    <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.6 8.4 6.3 14.1z"/>
                    <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.3c-2 1.5-4.6 2.7-7.5 2.7-5.3 0-9.7-3.4-11.3-8l-6.6 5.1C9.5 39.6 16.2 44 24 44z"/>
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.5 5.3C41.4 35.8 44 30.3 44 24c0-1.2-.1-2.4-.4-3.5z"/>
                  </svg>
                  Continue with Google
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  {mode === 'signin' ? 'No account?' : 'Already have one?'}{' '}
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
                  >
                    {mode === 'signin' ? 'Create one' : 'Sign in'}
                  </button>
                </p>
              </form>
            </TabsContent>
          </Tabs>
          <p className="mt-6 text-center text-[11px] text-muted-foreground/70">
            Built for modern automotive sales teams.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
